/**
 * React error boundary that swallows render exceptions, reports to Sentry, and
 * renders fallback UI in their place.
 *
 * The single fallback for component-level failures across the platform:
 * features wrap renders in `<Catcher>` (or `<CatchList>` for keyed lists) so
 * one broken row does not blank the whole screen. Caught errors flow to
 * `captureException` from `util/sentry` and surface a minimal 'Something went
 * wrong' view. Test mode is special-cased twice over: `componentDidCatch` is
 * deleted from the prototype at module load when `NODE_ENV === 'test'`, and any
 * catch path explicitly rethrows — both are there so demos and Jest snapshots
 * fail loudly instead of swallowing real bugs.
 */

import React from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { Text, View } from 'react-native';
import { DatastoreContext } from 'util/datastoreContext';
import { captureException } from 'util/sentry';

type CatcherProps = {
    children: React.ReactNode;
    style?: StyleProp<ViewStyle>;
};

type CatcherState = {
    error: Error | null;
};

export class Catcher extends React.Component<CatcherProps, CatcherState> {
    static contextType = DatastoreContext;

    constructor(props: CatcherProps) {
        super(props);
        this.state = { error: null };
    }

    async componentDidCatch(error: any, errorInfo: any) {
        console.error('componentDidCatch', error, errorInfo);
        if (process.env.NODE_ENV === 'test') {
            throw error;
        }

        this.setState({ error });
        captureException(error);
    }

    render() {
        if (this.state.error) {
            return (
                <View style={this.props.style}>
                    {/* eslint-disable-next-line @repo/psi/no-literal-text-children */}
                    <Text>Something went wrong</Text>
                    <Text>{this.state.error.message}</Text>
                </View>
            );
        } else {
            return this.props.children;
        }
    }
}

// Remove componentDidCatch in test mode
if (process.env.NODE_ENV === 'test') {
    delete (Catcher.prototype as any).componentDidCatch;
}

type CatchListProps<T> = {
    items: T[];
    renderItem: (item: T, index: number, isLast: boolean) => React.ReactNode;
    getKey: (item: T) => string;
    separator?: () => React.ReactNode;
    showLastSeparator?: boolean;
    style?: StyleProp<ViewStyle>;
};

export function CatchList<T>({ items, renderItem, getKey, separator, showLastSeparator = false, style = undefined }: CatchListProps<T>) {
    return (
        <View style={style}>
            {items &&
                items.map((item, idx) => {
                    const isFinal = idx === items.length - 1;
                    const showSeparator = separator && (!isFinal || showLastSeparator);
                    return (
                        <Catcher key={getKey(item)}>
                            {renderItem(item, idx, isFinal)}
                            {showSeparator && separator()}
                        </Catcher>
                    );
                })}
        </View>
    );
}
